if (!customElements.get('size-chart-page')) {
  // Matches the custom element the size guide section wraps its blocks in.
  const sizeGuideSelector = 'size-guide-tabs';

  // Tab titles are matched against variant option values, so casing, spacing and
  // punctuation are stripped from both sides first.
  const normalize = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '');

  customElements.define(
    'size-chart-page',
    class SizeChartPage extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();

        const signal = this.abortController.signal;
        const modal = this.modal;

        // Keep the tab in step with the variant picker, which re-renders on selection.
        document.addEventListener('change', (event) => {
          if (event.target.closest('variant-picker')) this.selectTab();
        }, { signal });

        this.addEventListener('click', this.onItemClick.bind(this), { signal });

        if (!modal) {
          this.load();
          return;
        }

        if (modal.open) this.load();

        modal.addEventListener('modal:afterShow', () => {
          this.load();
          this.selectTab();
        }, { signal });

        // Warm the content up while the pointer is still on its way to the trigger.
        this.triggers.forEach((button) => {
          button.addEventListener('pointerenter', this.load.bind(this), { signal });
        });
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get modal() {
        // The modal moves to the body while open and back again on close, but it stays
        // an ancestor of this element throughout.
        return this.closest('x-modal');
      }

      get triggers() {
        const modal = this.modal;

        if (!modal) return [];

        return Array.from(document.querySelectorAll(`[aria-controls="${modal.id}"]`)).filter((button) => !modal.contains(button));
      }

      get picker() {
        // Resolved through the trigger because the picker is not an ancestor once the
        // modal has been moved to the body.
        return this.triggers[0]?.closest('variant-picker') ?? null;
      }

      get selectedOptionValues() {
        const picker = this.picker;

        if (!picker) return [];

        return Array.from(picker.querySelectorAll('input:checked, option:checked')).map((input) => normalize(input.value));
      }

      async load() {
        if (this.loading) return;
        this.loading = true;

        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) throw new Error(`Size chart page responded with ${response.status}`);

          const page = new DOMParser().parseFromString(await response.text(), 'text/html');

          // The whole section wrapper is kept: its styles are scoped to #shopify-section-{id}.
          const section = page.querySelector(sizeGuideSelector)?.closest('.shopify-section');

          if (!section) throw new Error(`No size guide section on ${this.dataset.url}`);

          // Scripts are inert once assigned through innerHTML, so they are pulled out and re-created.
          const scripts = Array.from(section.querySelectorAll('script'));
          scripts.forEach((script) => script.remove());

          // Re-parsing in the live document upgrades the custom elements the section relies on.
          this.innerHTML = section.outerHTML;
          scripts.forEach(this.runScript);
          this.selectTab();
        } catch (error) {
          // Drop the spinner rather than leave it turning. The next open retries.
          this.innerHTML = '';
          this.loading = false;
          console.error(error);
        }
      }

      selectTab() {
        const tabs = this.querySelector(sizeGuideSelector);

        if (!tabs) return;

        const buttons = Array.from(tabs.querySelectorAll('button[role="tab"]'));
        const values = this.selectedOptionValues;
        const index = buttons.findIndex((button) => {
          const title = normalize(button.textContent);
          return title !== '' && values.includes(title);
        });

        if (index === -1) return;

        // Set through the attribute rather than the property: the section script may not
        // have upgraded the element yet, and an own property would shadow its setter.
        tabs.setAttribute('selected-index', index.toString());
      }

      onItemClick(event) {
        const button = event.target.closest('.size-guide__item a, .size-guide__item button');

        if (!button) return;

        const link = button.getAttribute('href');
        const url = link ? new URL(link, window.location.href) : null;

        // Entries linking somewhere else are left alone: only the ones pointing back at
        // this product act as variant shortcuts.
        if (url && url.pathname !== window.location.pathname) return;

        event.preventDefault();

        this.selectVariant(url?.searchParams.get('variant') ?? this.variantIdFor(button));
        this.modal?.hide();

        // With a design already on the canvas the entry is only a variant shortcut: the
        // shape and size follow the new variant and the artwork stays put. Asking for a
        // file again here would throw that design away.
        if (SizeChartPage.hasDesign) return;

        // Kept in the same task as the click so the file dialog still counts as user
        // initiated. The visible upload button submits the form, so the input is opened
        // directly instead.
        document.querySelector('#custom-rug-image')?.click();
      }

      // The canvas container is revealed the moment a file is picked (see
      // CustomRugCanvas._revealBuilder), which is the same signal product-info uses to
      // know a design is in progress.
      static get hasDesign() {
        const container = document.querySelector('.product-main-canvas-container');
        return !!container && container.style.display !== 'none';
      }

      variantIdFor(button) {
        const variants = this.picker?.getVariantData() ?? [];

        if (variants.length === 0) return null;

        const panel = button.closest('[role="tabpanel"]');
        const tab = panel ? this.querySelector(`#${CSS.escape(panel.getAttribute('aria-labelledby'))}`) : null;
        const item = button.closest('.size-guide__item');

        // The tab names the shape; the heading and the button itself name the size. The
        // description is deliberately left out, since its prose mentions sizes the entry
        // does not link to.
        const shape = normalize(tab?.textContent ?? '');
        const label = normalize(`${item?.querySelector('.heading')?.textContent ?? ''} ${button.textContent}`);

        const scored = [];

        variants.forEach((variant) => {
          const options = [variant.option1, variant.option2, variant.option3].filter(Boolean).map(normalize);
          const shapeIndex = shape === '' ? -1 : options.indexOf(shape);

          if (shape !== '' && shapeIndex === -1) return;

          const size = options.find((value, index) => index !== shapeIndex && value !== '' && label.includes(value));

          if (!size) return;

          // Longest size always wins, so "Select 2.5'x7'" is not settled by "5'x7'".
          // Availability only breaks ties between variants holding the same size.
          scored.push({ id: variant.id, weight: size.length * 1000 + (variant.available ? 1 : 0) });
        });

        return scored.sort((a, b) => b.weight - a.weight)[0]?.id.toString() ?? null;
      }

      selectVariant(variantId) {
        const picker = this.picker;
        const productInfo = picker?.closest('product-info');

        if (!variantId || !productInfo) return;

        const productUrl = productInfo.getAttribute('data-product-url');
        const variant = picker.getVariantData().find((entry) => entry.id.toString() === variantId);

        // Deep linked the same way the theme does elsewhere: the section render resolves
        // ?variant= server side, so every option comes back selected in one pass. Picking
        // the options here instead would race, since each shape renders its own sizes.
        productInfo.updateURL(variantId);
        productInfo.updateCanvasVariant(variant);
        productInfo.renderProductInfo({
          requestUrl: `${productUrl}?variant=${variantId}&section_id=${productInfo.sectionId}`,
          targetId: '',
          callback: productInfo.handleUpdateProductInfo()
        });
      }

      runScript(script) {
        if (script.src && Array.from(document.scripts).some((loaded) => loaded.src === script.src)) return;

        const clone = document.createElement('script');

        if (script.src) {
          clone.src = script.src;
        } else {
          clone.textContent = script.textContent;
        }

        document.head.appendChild(clone);
      }
    }
  );
}
